import { useState, useEffect, useRef } from 'react';
import { templateApi } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { SearchInput } from '../components/ui/SearchInput';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import { WorkflowReadOnlyCanvas } from '../components/workflow/WorkflowReadOnlyCanvas';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  FileText,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Trash2,
  Star,
  User,
  Eye,
  Plus,
  Upload,
  X,
  GitBranch,
  Calendar,
  Download,
  Import,
  Edit3,
  Languages,
} from 'lucide-react';

interface I18nField<T = string> {
  zh: T;
  en: T;
  ja?: T;
}

interface Template {
  id: string;
  name: string | I18nField;
  description?: string | I18nField;
  authorId: string;
  authorName: string;
  downloads: number;
  rating: number;
  status: string; // active, inactive, draft
  category?: string;
  industry?: string;
  tags?: string[] | I18nField<string[]>;
  nodeCount?: number;
  usageCount?: number;
  workflowDefinition?: {
    nodes: Array<{
      id: string;
      type: string;
      label: string;
      position?: { x: number; y: number };
      config?: Record<string, unknown>;
    }>;
    edges: Array<{
      id?: string;
      source: string;
      target: string;
    }>;
  };
  definition?: {
    nodes: Array<{
      id: string;
      type: string;
      label: string;
      position?: { x: number; y: number };
      config?: Record<string, unknown>;
    }>;
    edges: Array<{
      id?: string;
      source: string;
      target: string;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);
  
  // 新建模板相关状态
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    nameZh: '',
    nameEn: '',
    nameJa: '',
    descriptionZh: '',
    descriptionEn: '',
    descriptionJa: '',
    category: 'system',
    industry: '',
    tagsZh: '',
    tagsEn: '',
    tagsJa: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [i18nTab, setI18nTab] = useState<'zh' | 'en' | 'ja'>('zh');
  const [isAutoTranslating, setIsAutoTranslating] = useState(false);
  
  // 查看详情状态
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // 删除确认对话框状态
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  
  // 导入相关状态
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 编辑相关状态
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editForm, setEditForm] = useState({
    nameZh: '',
    nameEn: '',
    nameJa: '',
    descriptionZh: '',
    descriptionEn: '',
    descriptionJa: '',
    category: 'system',
    industry: '',
    tagsZh: '',
    tagsEn: '',
    tagsJa: '',
  });
  const [selectedWorkflowFile, setSelectedWorkflowFile] = useState<File | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [editI18nTab, setEditI18nTab] = useState<'zh' | 'en' | 'ja'>('zh');

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await templateApi.listTemplates({
        page,
        pageSize,
        keyword: keyword || undefined,
        status: status || undefined,
      });
      if (response.code === 0) {
        setTemplates(response.data.items as Template[]);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [page, pageSize, keyword, status]);

  const handleDelete = (template: Template) => {
    setTemplateToDelete(template);
    setDeleteConfirmName('');
    setDeleteError('');
    setShowDeleteModal(true);
    setShowActionMenu(null);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    
    // 验证模板名称是否匹配（使用中文名称）
    const templateName = getI18nValue(templateToDelete.name, 'zh');
    if (deleteConfirmName.trim() !== templateName) {
      setDeleteError('模板名称不匹配，请输入正确的模板名称');
      return;
    }
    
    setIsDeleting(true);
    try {
      await templateApi.deleteTemplate(templateToDelete.id);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      setDeleteConfirmName('');
      fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
      setDeleteError('删除失败，请稍后重试');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
    setDeleteConfirmName('');
    setDeleteError('');
  };

  // 新建模板自动翻译
  const handleCreateAutoTranslate = async (field: 'name' | 'description' | 'tags') => {
    const sourceText = createForm[`${field}Zh` as keyof typeof createForm] as string;
    if (!sourceText.trim()) {
      alert('请先输入中文内容');
      return;
    }

    setIsAutoTranslating(true);
    try {
      const response = await templateApi.translateTemplateFields(sourceText, ['en', 'ja'], 'zh');
      if (response.code === 0 && response.data.translations) {
        const translations = response.data.translations;
        setCreateForm(prev => ({
          ...prev,
          [`${field}En`]: translations.en || prev[`${field}En` as keyof typeof prev],
          [`${field}Ja`]: translations.ja || prev[`${field}Ja` as keyof typeof prev],
        }));
        alert('翻译完成');
      }
    } catch (err) {
      console.error('Failed to translate:', err);
      alert('翻译失败');
    } finally {
      setIsAutoTranslating(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!createForm.nameZh.trim() || !createForm.nameEn.trim()) {
      alert('请输入中文和英文模板名称');
      return;
    }

    setIsCreating(true);
    try {
      const tagsZh = createForm.tagsZh.split(',').map(t => t.trim()).filter(Boolean);
      const tagsEn = createForm.tagsEn.split(',').map(t => t.trim()).filter(Boolean);
      const tagsJa = createForm.tagsJa.split(',').map(t => t.trim()).filter(Boolean);
      await templateApi.createTemplate({
        name: { 
          zh: createForm.nameZh, 
          en: createForm.nameEn,
          ja: createForm.nameJa || undefined,
        },
        description: { 
          zh: createForm.descriptionZh, 
          en: createForm.descriptionEn,
          ja: createForm.descriptionJa || undefined,
        },
        category: createForm.category,
        industry: createForm.industry,
        tags: { 
          zh: tagsZh, 
          en: tagsEn,
          ja: tagsJa.length > 0 ? tagsJa : undefined,
        },
        file: selectedFile || undefined,
      });
      setShowCreateModal(false);
      setCreateForm({
        nameZh: '',
        nameEn: '',
        nameJa: '',
        descriptionZh: '',
        descriptionEn: '',
        descriptionJa: '',
        category: 'system',
        industry: '',
        tagsZh: '',
        tagsEn: '',
        tagsJa: '',
      });
      setSelectedFile(null);
      setI18nTab('zh');
      fetchTemplates();
    } catch (err) {
      console.error('Failed to create template:', err);
      alert('创建模板失败');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const fetchTemplateDetail = async (templateId: string) => {
    try {
      const response = await templateApi.getTemplateDetail(templateId);
      if (response.code === 0) {
        // 将后端返回的 definition 映射为 workflowDefinition
        const data = response.data;
        const template: Template = {
          ...data,
          workflowDefinition: data.definition,
        };
        setSelectedTemplate(template);
        setShowDetailModal(true);
      }
    } catch (err) {
      console.error('Failed to fetch template detail:', err);
      alert('获取模板详情失败');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('zh-CN');
  };

  // 辅助函数：获取 i18n 字段的值
  const getI18nValue = (value: string | I18nField | undefined, lang: 'zh' | 'en' | 'ja' = 'zh'): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value[lang] || value.zh || '';
  };

  const getI18nArray = (value: string[] | I18nField<string[]> | undefined, lang: 'zh' | 'en' | 'ja' = 'zh'): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return value[lang] || value.zh || [];
  };

  // 处理打开编辑模态框
  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setEditForm({
      nameZh: getI18nValue(template.name, 'zh'),
      nameEn: getI18nValue(template.name, 'en'),
      nameJa: getI18nValue(template.name, 'ja') || '',
      descriptionZh: getI18nValue(template.description, 'zh'),
      descriptionEn: getI18nValue(template.description, 'en'),
      descriptionJa: getI18nValue(template.description, 'ja') || '',
      category: template.category || 'system',
      industry: template.industry || '',
      tagsZh: getI18nArray(template.tags, 'zh').join(', '),
      tagsEn: getI18nArray(template.tags, 'en').join(', '),
      tagsJa: getI18nArray(template.tags, 'ja').join(', '),
    });
    setSelectedWorkflowFile(null);
    setEditI18nTab('zh');
    setShowEditModal(true);
    setShowActionMenu(null);
  };

  // 处理自动翻译
  const handleAutoTranslate = async (field: 'name' | 'description' | 'tags') => {
    if (!editingTemplate) return;
    
    const sourceText = editForm[`${field}Zh` as keyof typeof editForm] as string;
    if (!sourceText.trim()) {
      alert('请先输入中文内容');
      return;
    }

    setIsTranslating(true);
    try {
      const response = await templateApi.translateTemplateFields(sourceText, ['en', 'ja'], 'zh');
      if (response.code === 0 && response.data.translations) {
        const translations = response.data.translations;
        setEditForm(prev => ({
          ...prev,
          [`${field}En`]: translations.en || prev[`${field}En` as keyof typeof prev],
          [`${field}Ja`]: translations.ja || prev[`${field}Ja` as keyof typeof prev],
        }));
        alert('翻译完成');
      }
    } catch (err) {
      console.error('Failed to translate:', err);
      alert('翻译失败');
    } finally {
      setIsTranslating(false);
    }
  };

  // 处理更新模板
  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    if (!editForm.nameZh.trim() || !editForm.nameEn.trim()) {
      alert('请输入中文和英文模板名称');
      return;
    }

    setIsUpdating(true);
    try {
      const tagsZh = editForm.tagsZh.split(',').map(t => t.trim()).filter(Boolean);
      const tagsEn = editForm.tagsEn.split(',').map(t => t.trim()).filter(Boolean);
      const tagsJa = editForm.tagsJa.split(',').map(t => t.trim()).filter(Boolean);
      
      await templateApi.updateTemplate(editingTemplate.id, {
        name: { 
          zh: editForm.nameZh, 
          en: editForm.nameEn,
          ja: editForm.nameJa || undefined,
        },
        description: { 
          zh: editForm.descriptionZh, 
          en: editForm.descriptionEn,
          ja: editForm.descriptionJa || undefined,
        },
        category: editForm.category,
        industry: editForm.industry,
        tags: { 
          zh: tagsZh, 
          en: tagsEn,
          ja: tagsJa.length > 0 ? tagsJa : undefined,
        },
        file: selectedWorkflowFile || undefined,
      });
      
      setShowEditModal(false);
      setEditingTemplate(null);
      setSelectedWorkflowFile(null);
      fetchTemplates();
      alert('模板更新成功');
    } catch (err) {
      console.error('Failed to update template:', err);
      alert('更新模板失败');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExport = async (template: Template) => {
    try {
      const fileName = getI18nValue(template.name, 'zh');
      await templateApi.exportTemplate(template.id, fileName);
    } catch (err) {
      console.error('Failed to export template:', err);
      alert('导出模板失败');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      await templateApi.importTemplate(file);
      alert('导入模板成功');
      fetchTemplates();
    } catch (err) {
      console.error('Failed to import template:', err);
      alert('导入模板失败');
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">已上线</Badge>;
      case 'inactive':
        return <Badge variant="error">已下线</Badge>;
      case 'draft':
        return <Badge variant="warning">草稿</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const handleToggleStatus = async (template: Template) => {
    const newStatus = template.status === 'active' ? 'inactive' : 'active';
    try {
      await templateApi.updateTemplateStatus(template.id, newStatus);
      fetchTemplates();
    } catch (err) {
      console.error('Failed to update template status:', err);
      alert('更新模板状态失败');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">模板市场</h1>
          <p className="text-zinc-500 mt-1">管理模板审核和发布</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportFileChange}
            accept=".json"
            className="hidden"
          />
          <Button 
            variant="secondary" 
            onClick={handleImportClick}
            disabled={isImporting}
          >
            <Import className="w-4 h-4" />
            {isImporting ? '导入中...' : '导入'}
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4" />
            新建模板
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              placeholder="搜索模板名称..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onClear={() => setKeyword('')}
              className="sm:w-80"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
            >
              <option value="">所有状态</option>
              <option value="active">已上线</option>
              <option value="inactive">已下线</option>
              <option value="draft">草稿</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card>
        <CardHeader>
          <CardTitle>模板列表</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>模板</TableHead>
                    <TableHead>作者</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>下载</TableHead>
                    <TableHead>评分</TableHead>
                    <TableHead>创建时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-accent-600" />
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900">{getI18nValue(template.name, 'zh')}</p>
                            <p className="text-sm text-zinc-500 truncate max-w-[200px]">{getI18nValue(template.description, 'zh') || '无描述'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-400" />
                          <span>{template.authorName}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(template.status)}</TableCell>
                      <TableCell>{template.downloads}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-500" />
                          <span>{(template.rating || 0).toFixed(1)}</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(template.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="relative inline-block">
                          <button
                            onClick={() => setShowActionMenu(showActionMenu === template.id ? null : template.id)}
                            className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          
                          {showActionMenu === template.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg border border-zinc-200 shadow-lg z-50">
                              <button
                                onClick={() => {
                                  fetchTemplateDetail(template.id);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                              >
                                <Eye className="w-4 h-4" />
                                查看详情
                              </button>
                              <button
                                onClick={() => {
                                  handleEdit(template);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                              >
                                <Edit3 className="w-4 h-4" />
                                编辑
                              </button>
                              <button
                                onClick={() => {
                                  handleToggleStatus(template);
                                  setShowActionMenu(null);
                                }}
                                className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
                                  template.status === 'active'
                                    ? 'text-error-600 hover:bg-error-50'
                                    : 'text-success-600 hover:bg-success-50'
                                }`}
                              >
                                {template.status === 'active' ? (
                                  <>
                                    <XCircle className="w-4 h-4" />
                                    下线
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4" />
                                    上线
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  handleExport(template);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                              >
                                <Download className="w-4 h-4" />
                                导出
                              </button>
                              <button
                                onClick={() => handleDelete(template)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error-600 hover:bg-error-50"
                              >
                                <Trash2 className="w-4 h-4" />
                                删除
                              </button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <h2 className="text-xl font-semibold text-zinc-900">新建模板</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* i18n Tab Switcher */}
              <div className="flex items-center gap-2 border-b border-zinc-200">
                {(['zh', 'en', 'ja'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setI18nTab(lang)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      i18nTab === lang
                        ? 'border-accent-500 text-accent-600'
                        : 'border-transparent text-zinc-500 hover:text-zinc-700'
                    }`}
                  >
                    {lang === 'zh' ? '中文 (ZH)' : lang === 'en' ? 'English (EN)' : '日本語 (JA)'}
                  </button>
                ))}
              </div>

              {/* Template Name - i18n */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-zinc-700">
                    模板名称 {i18nTab === 'zh' ? '(中文)' : i18nTab === 'en' ? '(English)' : '(日本語)'}
                    {i18nTab === 'zh' && <span className="text-red-500">*</span>}
                  </label>
                  {i18nTab === 'zh' && (
                    <button
                      type="button"
                      onClick={() => handleCreateAutoTranslate('name')}
                      disabled={isAutoTranslating}
                      className="text-xs text-accent-600 hover:text-accent-700 flex items-center gap-1"
                    >
                      <Languages className="w-3 h-3" />
                      {isAutoTranslating ? '翻译中...' : '自动翻译'}
                    </button>
                  )}
                </div>
                <Input
                  value={createForm[i18nTab === 'zh' ? 'nameZh' : i18nTab === 'en' ? 'nameEn' : 'nameJa']}
                  onChange={(e) => setCreateForm({ 
                    ...createForm, 
                    [i18nTab === 'zh' ? 'nameZh' : i18nTab === 'en' ? 'nameEn' : 'nameJa']: e.target.value 
                  })}
                  placeholder={i18nTab === 'zh' ? '输入中文模板名称' : i18nTab === 'en' ? 'Enter English template name' : '日本語のテンプレート名を入力'}
                />
              </div>

              {/* Description - i18n */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-zinc-700">
                    模板描述 {i18nTab === 'zh' ? '(中文)' : i18nTab === 'en' ? '(English)' : '(日本語)'}
                  </label>
                  {i18nTab === 'zh' && (
                    <button
                      type="button"
                      onClick={() => handleCreateAutoTranslate('description')}
                      disabled={isAutoTranslating}
                      className="text-xs text-accent-600 hover:text-accent-700 flex items-center gap-1"
                    >
                      <Languages className="w-3 h-3" />
                      {isAutoTranslating ? '翻译中...' : '自动翻译'}
                    </button>
                  )}
                </div>
                <textarea
                  value={createForm[i18nTab === 'zh' ? 'descriptionZh' : i18nTab === 'en' ? 'descriptionEn' : 'descriptionJa']}
                  onChange={(e) => setCreateForm({ 
                    ...createForm, 
                    [i18nTab === 'zh' ? 'descriptionZh' : i18nTab === 'en' ? 'descriptionEn' : 'descriptionJa']: e.target.value 
                  })}
                  placeholder={i18nTab === 'zh' ? '输入中文模板描述' : i18nTab === 'en' ? 'Enter English template description' : '日本語のテンプレート説明を入力'}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 resize-none"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  分类
                </label>
                <select
                  value={createForm.category}
                  onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                >
                  <option value="system">系统模板</option>
                  <option value="custom">自定义模板</option>
                  <option value="industry">行业模板</option>
                </select>
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  行业
                </label>
                <Input
                  value={createForm.industry}
                  onChange={(e) => setCreateForm({ ...createForm, industry: e.target.value })}
                  placeholder="例如：零售、金融、制造等"
                />
              </div>

              {/* Tags - i18n */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-zinc-700">
                    标签 {i18nTab === 'zh' ? '(中文)' : i18nTab === 'en' ? '(English)' : '(日本語)'}
                  </label>
                  {i18nTab === 'zh' && (
                    <button
                      type="button"
                      onClick={() => handleCreateAutoTranslate('tags')}
                      disabled={isAutoTranslating}
                      className="text-xs text-accent-600 hover:text-accent-700 flex items-center gap-1"
                    >
                      <Languages className="w-3 h-3" />
                      {isAutoTranslating ? '翻译中...' : '自动翻译'}
                    </button>
                  )}
                </div>
                <Input
                  value={createForm[i18nTab === 'zh' ? 'tagsZh' : i18nTab === 'en' ? 'tagsEn' : 'tagsJa']}
                  onChange={(e) => setCreateForm({ 
                    ...createForm, 
                    [i18nTab === 'zh' ? 'tagsZh' : i18nTab === 'en' ? 'tagsEn' : 'tagsJa']: e.target.value 
                  })}
                  placeholder={i18nTab === 'zh' ? '输入标签，用逗号分隔' : i18nTab === 'en' ? 'Enter tags, separated by commas' : 'タグをカンマ区切りで入力'}
                />
                <p className="text-xs text-zinc-500 mt-1">
                  {i18nTab === 'zh' 
                    ? '多个标签用逗号分隔，如：销售,报表,数据分析' 
                    : i18nTab === 'en'
                    ? 'Multiple tags separated by commas, e.g.: sales,report,data-analysis'
                    : '複数のタグはカンマで区切ってください。例：販売,レポート,データ分析'}
                </p>
              </div>

              {/* Workflow File Upload */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  导入工作流（可选）
                </label>
                <div className="border-2 border-dashed border-zinc-300 rounded-lg p-4 text-center hover:border-accent-500 transition-colors">
                  <input
                    type="file"
                    accept=".zip,.bgl"
                    onChange={handleFileChange}
                    className="hidden"
                    id="workflow-file"
                  />
                  <label htmlFor="workflow-file" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                    <p className="text-sm text-zinc-600">
                      {selectedFile ? selectedFile.name : '点击上传工作流文件 (.zip)'}
                    </p>
                  </label>
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  支持从导出的工作流文件导入，自动转换为模板
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-200">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                取消
              </Button>
              <Button onClick={handleCreateTemplate} disabled={isCreating}>
                {isCreating ? '创建中...' : '创建模板'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template Detail Modal */}
      {showDetailModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <div>
                <h2 className="text-xl font-semibold text-zinc-900">{getI18nValue(selectedTemplate.name, 'zh')}</h2>
                <p className="text-sm text-zinc-500 mt-1">{getI18nValue(selectedTemplate.name, 'en')}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-zinc-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <User className="w-4 h-4" />
                    <span className="text-sm">作者</span>
                  </div>
                  <p className="font-medium text-zinc-900">{selectedTemplate.authorName}</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">创建时间</span>
                  </div>
                  <p className="font-medium text-zinc-900">{formatDate(selectedTemplate.createdAt)}</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <Download className="w-4 h-4" />
                    <span className="text-sm">使用次数</span>
                  </div>
                  <p className="font-medium text-zinc-900">{selectedTemplate.usageCount || 0}</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <Star className="w-4 h-4" />
                    <span className="text-sm">评分</span>
                  </div>
                  <p className="font-medium text-zinc-900">{(selectedTemplate.rating || 0).toFixed(1)}</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <span className="text-sm">状态</span>
                  </div>
                  <div className="mt-1">{getStatusBadge(selectedTemplate.status)}</div>
                </div>
              </div>

              {/* Description */}
              {selectedTemplate.description && (
                <div>
                  <h3 className="text-sm font-medium text-zinc-700 mb-2">模板描述</h3>
                  <div className="space-y-2">
                    <div className="bg-zinc-50 rounded-lg p-4">
                      <span className="text-xs text-zinc-400 mb-1 block">中文</span>
                      <p className="text-zinc-600">{getI18nValue(selectedTemplate.description, 'zh')}</p>
                    </div>
                    <div className="bg-zinc-50 rounded-lg p-4">
                      <span className="text-xs text-zinc-400 mb-1 block">English</span>
                      <p className="text-zinc-600">{getI18nValue(selectedTemplate.description, 'en')}</p>
                    </div>
                    {getI18nValue(selectedTemplate.description, 'ja') && (
                      <div className="bg-zinc-50 rounded-lg p-4">
                        <span className="text-xs text-zinc-400 mb-1 block">日本語</span>
                        <p className="text-zinc-600">{getI18nValue(selectedTemplate.description, 'ja')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {selectedTemplate.tags && (
                <div>
                  <h3 className="text-sm font-medium text-zinc-700 mb-2">标签</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-zinc-400 mb-1 block">中文</span>
                      <div className="flex flex-wrap gap-2">
                        {getI18nArray(selectedTemplate.tags, 'zh').map((tag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-accent-50 text-accent-700 text-sm rounded-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-zinc-400 mb-1 block">English</span>
                      <div className="flex flex-wrap gap-2">
                        {getI18nArray(selectedTemplate.tags, 'en').map((tag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-accent-50 text-accent-700 text-sm rounded-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    {getI18nArray(selectedTemplate.tags, 'ja').length > 0 && (
                      <div>
                        <span className="text-xs text-zinc-400 mb-1 block">日本語</span>
                        <div className="flex flex-wrap gap-2">
                          {getI18nArray(selectedTemplate.tags, 'ja').map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-accent-50 text-accent-700 text-sm rounded-md">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Workflow Structure */}
              {(selectedTemplate.workflowDefinition?.nodes?.length || selectedTemplate.definition?.nodes?.length) ? (
                <div>
                  <h3 className="text-sm font-medium text-zinc-700 mb-3 flex items-center gap-2">
                    <GitBranch className="w-4 h-4" />
                    工作流图示
                    <span className="text-zinc-400 font-normal">
                      ({(selectedTemplate.workflowDefinition?.nodes?.length || selectedTemplate.definition?.nodes?.length || 0)} 个节点)
                    </span>
                  </h3>
                  <div className="h-[400px] border border-zinc-200 rounded-xl overflow-hidden">
                    <WorkflowReadOnlyCanvas 
                      key={selectedTemplate.id}
                      nodes={(selectedTemplate.workflowDefinition?.nodes || selectedTemplate.definition?.nodes || []).map(n => ({
                        id: n.id,
                        type: n.type,
                        label: n.label,
                        position: n.position,
                        config: n.config,
                      }))}
                      edges={(selectedTemplate.workflowDefinition?.edges || selectedTemplate.definition?.edges || []).map(e => ({
                        from: e.source,
                        to: e.target,
                      }))}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-sm font-medium text-zinc-700 mb-3 flex items-center gap-2">
                    <GitBranch className="w-4 h-4" />
                    工作流图示
                  </h3>
                  <div className="bg-zinc-50 rounded-lg p-8 text-center">
                    <p className="text-zinc-500">此模板没有关联的工作流数据</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 p-6 border-t border-zinc-200">
              <Button 
                variant={selectedTemplate.status === 'active' ? 'danger' : 'primary'}
                onClick={() => {
                  handleToggleStatus(selectedTemplate);
                  setShowDetailModal(false);
                }}
              >
                {selectedTemplate.status === 'active' ? (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    下线模板
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    上线模板
                  </>
                )}
              </Button>
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteModal && templateToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-error-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-error-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-zinc-900">删除模板</h2>
                  <p className="text-sm text-zinc-500">此操作不可撤销</p>
                </div>
              </div>
              <button
                onClick={handleCancelDelete}
                className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <p className="text-sm text-error-800">
                  您即将删除模板 <strong>"{getI18nValue(templateToDelete.name, 'zh')}"</strong>。此操作将永久删除该模板及其所有相关数据，无法恢复。
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  请输入模板名称 <strong>"{getI18nValue(templateToDelete.name, 'zh')}"</strong> 以确认删除：
                </label>
                <Input
                  value={deleteConfirmName}
                  onChange={(e) => {
                    setDeleteConfirmName(e.target.value);
                    setDeleteError('');
                  }}
                  placeholder={`输入: ${getI18nValue(templateToDelete.name, 'zh')}`}
                  className={deleteError ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}
                />
                {deleteError && (
                  <p className="mt-2 text-sm text-error-600 flex items-center gap-1">
                    <XCircle className="w-4 h-4" />
                    {deleteError}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-200">
              <Button variant="secondary" onClick={handleCancelDelete} disabled={isDeleting}>
                取消
              </Button>
              <Button 
                variant="danger" 
                onClick={handleConfirmDelete} 
                disabled={isDeleting || deleteConfirmName.trim() !== getI18nValue(templateToDelete.name, 'zh')}
              >
                {isDeleting ? '删除中...' : '确认删除'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {showEditModal && editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <div>
                <h2 className="text-xl font-semibold text-zinc-900">编辑模板</h2>
                <p className="text-sm text-zinc-500 mt-1">修改模板信息和多语言内容</p>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTemplate(null);
                  setSelectedWorkflowFile(null);
                }}
                className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* i18n Tab Switcher */}
              <div className="flex items-center gap-2 border-b border-zinc-200">
                {(['zh', 'en', 'ja'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setEditI18nTab(lang)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      editI18nTab === lang
                        ? 'border-accent-500 text-accent-600'
                        : 'border-transparent text-zinc-500 hover:text-zinc-700'
                    }`}
                  >
                    {lang === 'zh' ? '中文 (ZH)' : lang === 'en' ? 'English (EN)' : '日本語 (JA)'}
                  </button>
                ))}
              </div>

              {/* Template Name */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-zinc-700">
                    模板名称 {editI18nTab === 'zh' ? '(中文)' : editI18nTab === 'en' ? '(English)' : '(日本語)'}
                    {editI18nTab === 'zh' && <span className="text-red-500">*</span>}
                  </label>
                  {editI18nTab === 'zh' && (
                    <button
                      type="button"
                      onClick={() => handleAutoTranslate('name')}
                      disabled={isTranslating}
                      className="text-xs text-accent-600 hover:text-accent-700 flex items-center gap-1"
                    >
                      <Languages className="w-3 h-3" />
                      {isTranslating ? '翻译中...' : '自动翻译'}
                    </button>
                  )}
                </div>
                <Input
                  value={editForm[`name${editI18nTab.charAt(0).toUpperCase() + editI18nTab.slice(1)}` as keyof typeof editForm]}
                  onChange={(e) => setEditForm(prev => ({
                    ...prev,
                    [`name${editI18nTab.charAt(0).toUpperCase() + editI18nTab.slice(1)}`]: e.target.value
                  }))}
                  placeholder={editI18nTab === 'zh' ? '输入中文模板名称' : editI18nTab === 'en' ? 'Enter English template name' : '日本語のテンプレート名を入力'}
                />
              </div>

              {/* Description */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-zinc-700">
                    模板描述 {editI18nTab === 'zh' ? '(中文)' : editI18nTab === 'en' ? '(English)' : '(日本語)'}
                  </label>
                  {editI18nTab === 'zh' && (
                    <button
                      type="button"
                      onClick={() => handleAutoTranslate('description')}
                      disabled={isTranslating}
                      className="text-xs text-accent-600 hover:text-accent-700 flex items-center gap-1"
                    >
                      <Languages className="w-3 h-3" />
                      {isTranslating ? '翻译中...' : '自动翻译'}
                    </button>
                  )}
                </div>
                <textarea
                  value={editForm[`description${editI18nTab.charAt(0).toUpperCase() + editI18nTab.slice(1)}` as keyof typeof editForm]}
                  onChange={(e) => setEditForm(prev => ({
                    ...prev,
                    [`description${editI18nTab.charAt(0).toUpperCase() + editI18nTab.slice(1)}`]: e.target.value
                  }))}
                  placeholder={editI18nTab === 'zh' ? '输入中文模板描述' : editI18nTab === 'en' ? 'Enter English template description' : '日本語のテンプレート説明を入力'}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 resize-none"
                />
              </div>

              {/* Tags */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-zinc-700">
                    标签 {editI18nTab === 'zh' ? '(中文)' : editI18nTab === 'en' ? '(English)' : '(日本語)'}
                  </label>
                  {editI18nTab === 'zh' && (
                    <button
                      type="button"
                      onClick={() => handleAutoTranslate('tags')}
                      disabled={isTranslating}
                      className="text-xs text-accent-600 hover:text-accent-700 flex items-center gap-1"
                    >
                      <Languages className="w-3 h-3" />
                      {isTranslating ? '翻译中...' : '自动翻译'}
                    </button>
                  )}
                </div>
                <Input
                  value={editForm[`tags${editI18nTab.charAt(0).toUpperCase() + editI18nTab.slice(1)}` as keyof typeof editForm]}
                  onChange={(e) => setEditForm(prev => ({
                    ...prev,
                    [`tags${editI18nTab.charAt(0).toUpperCase() + editI18nTab.slice(1)}`]: e.target.value
                  }))}
                  placeholder={editI18nTab === 'zh' ? '输入标签，用逗号分隔' : editI18nTab === 'en' ? 'Enter tags, separated by commas' : 'タグをカンマ区切りで入力'}
                />
                <p className="text-xs text-zinc-500 mt-1">
                  {editI18nTab === 'zh' 
                    ? '多个标签用逗号分隔，如：销售,报表,数据分析' 
                    : editI18nTab === 'en'
                    ? 'Multiple tags separated by commas, e.g.: sales,report,data-analysis'
                    : '複数のタグはカンマで区切ってください。例：販売,レポート,データ分析'}
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  分类
                </label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                >
                  <option value="system">系统模板</option>
                  <option value="custom">自定义模板</option>
                  <option value="industry">行业模板</option>
                </select>
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  行业
                </label>
                <Input
                  value={editForm.industry}
                  onChange={(e) => setEditForm(prev => ({ ...prev, industry: e.target.value }))}
                  placeholder="例如：零售、金融、制造等"
                />
              </div>

              {/* Workflow File Upload */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  导入新工作流（可选，将覆盖现有工作流）
                </label>
                <div className="border-2 border-dashed border-zinc-300 rounded-lg p-4 text-center hover:border-accent-500 transition-colors">
                  <input
                    type="file"
                    accept=".zip"
                    onChange={(e) => setSelectedWorkflowFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="edit-workflow-file"
                  />
                  <label htmlFor="edit-workflow-file" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                    <p className="text-sm text-zinc-600">
                      {selectedWorkflowFile ? selectedWorkflowFile.name : '点击上传工作流文件 (.zip)'}
                    </p>
                  </label>
                </div>
                {selectedWorkflowFile && (
                  <p className="text-xs text-amber-600 mt-1">
                    警告：上传新工作流将覆盖现有工作流定义
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-200">
              <Button 
                variant="secondary" 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTemplate(null);
                  setSelectedWorkflowFile(null);
                }}
                disabled={isUpdating}
              >
                取消
              </Button>
              <Button onClick={handleUpdateTemplate} disabled={isUpdating}>
                {isUpdating ? '保存中...' : '保存修改'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
